import React, { useEffect, useState, useMemo } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import DownloadIcon from "@mui/icons-material/Download";
import * as XLSX from "xlsx";
import { getKitchenReportsLogs } from "../../../../services/Admin/ReportsApi";
import { getAiDrinks } from "../../../../services/Admin/AiDrinksApi";
import { getMenu } from "../../../../services/Admin/SalesScreenApi";
import { formatTime12Hour } from "../../../../utils/dateTimeFormat";

const orderTypeLabel = { tables: "طاولة", delivery: "توصيل", takeaway: "تيك أواي" };

const statusLabel = {
  in_prepration: "في التحضير",
  in_preparation: "في التحضير",
  pending: "جديد لم يحضر",
  pickup: "تم الاستلام من العميل",
};

const getStatusLabel = (status) => statusLabel[status] || status || "—";

const getTodayDateInput = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const parseTimeInput = (value, fallback) => {
  const match = String(value || "").trim().match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (!match) return fallback;

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  const seconds = Number(match[3] ?? 0);
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59 || seconds < 0 || seconds > 59) return fallback;

  return { hours, minutes, seconds };
};

const buildFilterDateTime = (dateValue, timeValue, fallbackTime) => {
  if (!dateValue) return null;

  const [year, month, day] = String(dateValue).split("-").map(Number);
  if (!year || !month || !day) return null;

  const parsedTime = parseTimeInput(timeValue, fallbackTime);
  const date = new Date(
    year,
    month - 1,
    day,
    parsedTime.hours,
    parsedTime.minutes,
    parsedTime.seconds || 0,
    0
  );

  return Number.isNaN(date.getTime()) ? null : date.getTime();
};

const getKitchenLogDateTime = (log) => {
  if (!log) return null;

  const directDateTime = log.received_at && String(log.received_at).includes("T")
    ? new Date(log.received_at).getTime()
    : null;

  if (directDateTime !== null && !Number.isNaN(directDateTime)) return directDateTime;

  return buildFilterDateTime(log.date, log.received_at, {
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
};

const headSx = {
  fontWeight: 700,
  bgcolor: "primary.main",
  color: "primary.contrastText",
  whiteSpace: "nowrap",
};

const KitchenLogRow = ({ log, drinksMap, productsMap }) => {
  const itemType = log.order_item?.drink_id ? "مشروب  AI" : "منتج";
  const drinkId = log.order_item?.drink_id;
  const productId = log.order_item?.product_id;
  const drinkName = drinkId ? (drinksMap[drinkId]?.drink_name ?? `ID: ${drinkId}`) : null;
  const productName = productId ? (productsMap[productId]?.name ?? `ID: ${productId}`) : null;
  const itemLabel = drinkName ?? productName ?? "—";

  return (
    <TableRow hover>
      <TableCell align="center">{log.id}</TableCell>
      <TableCell align="center">
        {log.chef ? (
          <Box>
            <Typography variant="body2" fontWeight={700}>{log.chef.name}</Typography>
            <Typography variant="caption" color="text.secondary">{log.chef.username}</Typography>
          </Box>
        ) : "—"}
      </TableCell>
      <TableCell align="center">{log.date ?? "—"}</TableCell>
      <TableCell align="center">{formatTime12Hour(log.received_at, { empty: "—" })}</TableCell>
      <TableCell align="center">{formatTime12Hour(log.finished_at, { empty: "—" })}</TableCell>
      <TableCell align="center">
        {log.duration_minutes != null ? (
          <Chip
            label={`${log.duration_minutes} د`}
            size="small"
            color={log.duration_minutes <= 2 ? "success" : log.duration_minutes <= 5 ? "warning" : "error"}
            sx={{ fontWeight: 700 }}
          />
        ) : "—"}
      </TableCell>
      <TableCell align="center">{log.quantity ?? "—"}</TableCell>
      <TableCell align="center">
        {log.order ? `#${log.order.general_increment}` : "—"}
      </TableCell>
      <TableCell align="center">
        {log.order ? (orderTypeLabel[log.order.order_type] || log.order.order_type) : "—"}
      </TableCell>
      <TableCell align="center">{log.order?.table_id ?? "—"}</TableCell>
      <TableCell align="center">{getStatusLabel(log.order?.status)}</TableCell>
      <TableCell align="center">
        <Chip label={itemType} size="small" variant="outlined" color="primary" />
      </TableCell>
      <TableCell align="center">{itemLabel}</TableCell>
      <TableCell align="center">{log.order_item?.quantity ?? "—"}</TableCell>
      <TableCell align="center">{log.order_item?.price ?? "—"}</TableCell>
      <TableCell align="center">{log.order_item?.total ?? "—"}</TableCell>
      <TableCell align="center">{getStatusLabel(log.order_item?.status)}</TableCell>
      <TableCell align="center">{log.order_item?.note ?? "—"}</TableCell>
      <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
        {formatTime12Hour(log.order_item?.open_time, { empty: "—" })}
      </TableCell>
      <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
        {formatTime12Hour(log.order_item?.close_time, { empty: "—" })}
      </TableCell>
    </TableRow>
  );
};

const ParistaOfrdersTap = () => {
  const [logs, setLogs] = useState([]);
  const [drinksMap, setDrinksMap] = useState({});
  const [productsMap, setProductsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fromDate, setFromDate] = useState(getTodayDateInput);
  const [toDate, setToDate] = useState(getTodayDateInput);
  const [fromTime, setFromTime] = useState("00:00");
  const [toTime, setToTime] = useState("23:59");
  const [chefName, setChefName] = useState("");
  const [itemName, setItemName] = useState("");
  const [search, setSearch] = useState("");

  const fetchLogs = async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const res = await getKitchenReportsLogs(filters);
      setLogs(res.data);
    } catch (err) {
      setError(err.message || "حدث خطأ أثناء تحميل البيانات");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const today = getTodayDateInput();

    fetchLogs({
      from_date: today,
      to_date: today,
    });
    getAiDrinks()
      .then((res) => {
        const arr = Array.isArray(res?.data) ? res.data : [];
        const map = {};
        arr.forEach((d) => { map[d.id] = d; });
        setDrinksMap(map);
      })
      .catch(() => {});
    getMenu()
      .then((res) => {
        const categories = Array.isArray(res?.data) ? res.data : [];
        const map = {};
        categories.forEach((cat) => {
          (cat.products ?? []).forEach((p) => { map[p.id] = p; });
        });
        setProductsMap(map);
      })
      .catch(() => {});
  }, []);

  const handleApplyDateFilter = () => {
    fetchLogs({
      ...(fromDate ? { from_date: fromDate } : {}),
      ...(toDate ? { to_date: toDate } : {}),
    });
  };

  const handleClearFilter = () => {
    const today = getTodayDateInput();

    setFromDate(today);
    setToDate(today);
    setFromTime("00:00");
    setToTime("23:59");
    setChefName("");
    setItemName("");
    setSearch("");
    fetchLogs({
      from_date: today,
      to_date: today,
    });
  };

  const chefNames = useMemo(() => {
    const names = logs.map((log) => log.chef?.name).filter(Boolean);
    return [...new Set(names)].sort();
  }, [logs]);

  const itemNames = useMemo(() => {
    const names = logs.map((log) => {
      const did = log.order_item?.drink_id;
      const pid = log.order_item?.product_id;
      if (did) return drinksMap[did]?.drink_name ?? null;
      if (pid) return productsMap[pid]?.name ?? null;
      return null;
    }).filter(Boolean);
    return [...new Set(names)].sort();
  }, [logs, drinksMap, productsMap]);

  const filteredLogs = useMemo(() => {
    const q = search.trim().toLowerCase();
    const fromDateTime = buildFilterDateTime(fromDate, fromTime, {
      hours: 0,
      minutes: 0,
      seconds: 0,
    });
    const toDateTime = buildFilterDateTime(toDate, toTime, {
      hours: 23,
      minutes: 59,
      seconds: 59,
    });
    const toDateTimeEnd = toDateTime === null ? null : toDateTime + 999;

    return logs.filter((log) => {
      const logDateTime = getKitchenLogDateTime(log);

      if (fromDateTime !== null && (logDateTime === null || logDateTime < fromDateTime)) return false;
      if (toDateTimeEnd !== null && (logDateTime === null || logDateTime > toDateTimeEnd)) return false;
      if (chefName && log.chef?.name !== chefName) return false;
      if (itemName) {
        const did = log.order_item?.drink_id;
        const pid = log.order_item?.product_id;
        const name = did
          ? (drinksMap[did]?.drink_name ?? null)
          : (pid ? (productsMap[pid]?.name ?? null) : null);
        if (name !== itemName) return false;
      }
      if (q) {
        const haystack = [
          log.id,
          log.chef?.name,
          log.chef?.username,
          log.date,
          log.received_at,
          log.finished_at,
          log.duration_minutes,
          log.quantity,
          log.order?.general_increment,
          log.order?.order_type,
          log.order?.table_id,
          log.order?.status,
          log.order_item?.id,
          log.order_item?.product_id,
          log.order_item?.drink_id,
          log.order_item?.drink_id ? drinksMap[log.order_item.drink_id]?.drink_name : null,
          log.order_item?.product_id ? productsMap[log.order_item.product_id]?.name : null,
          log.order_item?.quantity,
          log.order_item?.price,
          log.order_item?.note,
          log.order_item?.status,
        ]
          .filter((v) => v != null)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [logs, fromDate, fromTime, toDate, toTime, chefName, itemName, search, drinksMap, productsMap]);

  const handleExport = () => {
    const rows = filteredLogs.map((log) => ({
      "#": log.id,
      "الباريستا": log.chef?.name ?? "",
      "اسم المستخدم": log.chef?.username ?? "",
      "التاريخ": log.date ?? "",
      "وقت الاستلام": formatTime12Hour(log.received_at, { empty: "—" }),
      "وقت التسليم": formatTime12Hour(log.finished_at, { empty: "—" }),
      "مدة التحضير (د)": log.duration_minutes ?? "",
      "الكمية": log.quantity ?? "",
      "رقم الطلب": log.order?.general_increment ?? "",
      "نوع الطلب": log.order?.order_type ?? "",
      "الطاولة": log.order?.table_id ?? "",
      "حالة الطلب": getStatusLabel(log.order?.status),
      "نوع الصنف": log.order_item?.drink_id ? "مشروب  AI" : "منتج",
      "اسم الصنف": log.order_item?.drink_id
        ? (drinksMap[log.order_item.drink_id]?.drink_name ?? `ID: ${log.order_item.drink_id}`)
        : (log.order_item?.product_id
          ? (productsMap[log.order_item.product_id]?.name ?? `ID: ${log.order_item.product_id}`)
          : ""),
      "الكمية (الصنف)": log.order_item?.quantity ?? "",
      "السعر": log.order_item?.price ?? "",
      "الإجمالي": log.order_item?.total ?? "",
      "حالة الصنف": getStatusLabel(log.order_item?.status),
      "ملاحظة": log.order_item?.note ?? "",
      "وقت فتح الصنف": formatTime12Hour(log.order_item?.open_time, { empty: "—" }),
      "وقت إغلاق الصنف": formatTime12Hour(log.order_item?.close_time, { empty: "—" }),
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "تقارير الباريستا");
    XLSX.writeFile(wb, `تقارير_الباريستا_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress color="primary" />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>;
  }

  return (
    <Box>
      {/* Search Bar */}
      <TextField
        fullWidth
        size="small"
        placeholder="بحث في كل البيانات..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />,
        }}
        sx={{ mb: 2 }}
      />

      {/* Filter Bar */}
      <Paper
        elevation={0}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
          p: 2,
          mb: 2,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
        }}
      >
        <Typography variant="body2" fontWeight={700} color="text.secondary">
          تصفية:
        </Typography>
        <TextField
          label="من"
          type="date"
          size="small"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ max: toDate || undefined }}
          sx={{ minWidth: 160 }}
        />
        <TextField
          label="من وقت"
          type="time"
          size="small"
          value={fromTime}
          onChange={(e) => setFromTime(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ step: 60 }}
          sx={{ width: 125 }}
        />
        <TextField
          label="إلى"
          type="date"
          size="small"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ min: fromDate || undefined }}
          sx={{ minWidth: 160 }}
        />
        <TextField
          label="إلى وقت"
          type="time"
          size="small"
          value={toTime}
          onChange={(e) => setToTime(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ step: 60 }}
          sx={{ width: 125 }}
        />
        <Button
          size="small"
          variant="contained"
          color="primary"
          onClick={handleApplyDateFilter}
        >
          تطبيق
        </Button>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>الباريستا</InputLabel>
          <Select
            value={chefName}
            label="الباريستا"
            onChange={(e) => setChefName(e.target.value)}
          >
            <MenuItem value="">الكل</MenuItem>
            {chefNames.map((name) => (
              <MenuItem key={name} value={name}>{name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>اسم الصنف</InputLabel>
          <Select
            value={itemName}
            label="اسم الصنف"
            onChange={(e) => setItemName(e.target.value)}
          >
            <MenuItem value="">الكل</MenuItem>
            {itemNames.map((name) => (
              <MenuItem key={name} value={name}>{name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {(fromDate || toDate || chefName || itemName || search) && (
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            startIcon={<FilterAltOffIcon />}
            onClick={handleClearFilter}
          >
            إلغاء الفلتر
          </Button>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ mr: "auto" }}>
          يُعرض {filteredLogs.length} من {logs.length} سجل
        </Typography>
        <Button
          size="small"
          variant="contained"
          color="primary"
          startIcon={<DownloadIcon />}
          onClick={handleExport}
          disabled={filteredLogs.length === 0}
        >
          تصدير Excel
        </Button>
      </Paper>

      {filteredLogs.length === 0 ? (
        <Alert severity="info">لا توجد سجلات متاحة</Alert>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell align="center" sx={headSx}>#</TableCell>
                <TableCell align="center" sx={headSx}>الباريستا</TableCell>
                <TableCell align="center" sx={headSx}>التاريخ</TableCell>
                <TableCell align="center" sx={headSx}>وقت الاستلام</TableCell>
                <TableCell align="center" sx={headSx}>وقت التسليم</TableCell>
                <TableCell align="center" sx={headSx}>مدة التحضير</TableCell>
                <TableCell align="center" sx={headSx}>الكمية</TableCell>
                <TableCell align="center" sx={headSx}>رقم الطلب</TableCell>
                <TableCell align="center" sx={headSx}>نوع الطلب</TableCell>
                <TableCell align="center" sx={headSx}>الطاولة</TableCell>
                <TableCell align="center" sx={headSx}>حالة الطلب</TableCell>
                <TableCell align="center" sx={headSx}>نوع الصنف</TableCell>
                <TableCell align="center" sx={headSx}>اسم الصنف</TableCell>
                <TableCell align="center" sx={headSx}>الكمية</TableCell>
                <TableCell align="center" sx={headSx}>السعر</TableCell>
                <TableCell align="center" sx={headSx}>الإجمالي</TableCell>
                <TableCell align="center" sx={headSx}>حالة الصنف</TableCell>
                <TableCell align="center" sx={headSx}>ملاحظة</TableCell>
                <TableCell align="center" sx={headSx}>فتح الصنف</TableCell>
                <TableCell align="center" sx={headSx}>إغلاق الصنف</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredLogs.map((log) => (
                <KitchenLogRow key={log.id} log={log} drinksMap={drinksMap} productsMap={productsMap} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default ParistaOfrdersTap;
